// src/components/ctl/patterns/ctl-confirmation.js
//
// Checkout confirmation / success panel for storefront flows.
//
// Attributes:
// - status: "success" | "pending" | "error" (default "success")
// - title (string)
// - message (string)
// - reference (string)      // e.g., reservation id / confirmation code
// - primary-label (string)  // default varies by status
// - secondary-label (string) // optional
// - loading (boolean)
// - disabled (boolean)
//
// Events:
// - ctl-confirm (detail: { status })
// - ctl-cancel (detail: { status })
//
// Slots:
// - media     (optional) top icon/illustration
// - details   (optional) extra details block (e.g., address, hours, unit summary)
// - actions   (optional) custom actions (hides built-in buttons)
// - footer    (optional) legal/help text

import "../foundation/ctl-button.js";
import "../foundation/ctl-typography.js";
import "../foundation/ctl-badge.js";
import "../layouts/ctl-inline.js";
import "../foundation/ctl-skeleton.js";
import "../foundation/ctl-divider.js";

const template = document.createElement("template");

template.innerHTML = `
<style>
  :host{
    --font: var(--ctl-sf-font, var(--ctl-font-sans));
    --text: var(--ctl-sf-text, var(--ctl-color-text));
    --muted: var(--ctl-sf-muted, var(--ctl-color-muted));
    --surface: var(--ctl-sf-surface, var(--ctl-color-surface));
    --border: var(--ctl-sf-border, var(--ctl-color-border));
    --brand: var(--ctl-sf-brand, var(--ctl-color-brand));

    --radius: var(--ctl-radius-lg);
    --shadow: var(--ctl-shadow-sm);
    --focus: var(--ctl-focus-ring);

    display:block;
    font-family: var(--font);
    color: var(--text);
  }

  .card{
    border: 1px solid var(--border);
    background: var(--surface);
    border-radius: var(--radius);
    box-shadow: var(--shadow);
    overflow:hidden;
  }

  .body{
    padding: var(--ctl-space-4);
    display:grid;
    gap: var(--ctl-space-3);
  }

  .top{
    display:flex;
    gap: var(--ctl-space-3);
    align-items:flex-start;
  }

  .icon{
    width: 44px;
    height: 44px;
    border-radius: var(--ctl-radius-14);
    display:grid;
    place-items:center;
    border: 1px solid color-mix(in srgb, var(--border) 75%, transparent);
    background: color-mix(in srgb, var(--surface) 90%, var(--brand) 10%);
    flex: 0 0 auto;
  }

  .icon svg{
    width: var(--ctl-space-5-5);
    height: var(--ctl-space-5-5);
  }

  .title{
    margin: 0;
    font-size: var(--ctl-font-size-xl);
    font-weight: var(--ctl-font-weight-ultra);
    letter-spacing: -0.02em;
    line-height: 1.1;
  }

  .msg{
    margin: var(--ctl-space-1-5) 0 0;
    color: var(--muted);
    font-size: var(--ctl-font-size-md);
    line-height: 1.45;
  }

  .ref{
    margin-top: var(--ctl-space-2);
    display:flex;
    flex-wrap:wrap;
    gap: var(--ctl-space-2);
    align-items:center;
    color: var(--muted);
    font-size: var(--ctl-font-size-sm);
  }

  .code{
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    font-weight: var(--ctl-font-weight-extrabold);
    color: var(--text);
    background: color-mix(in srgb, var(--border) 20%, transparent);
    border: 1px solid color-mix(in srgb, var(--border) 70%, transparent);
    padding: var(--ctl-space-1) var(--ctl-space-2);
    border-radius: var(--ctl-radius-10);
  }

  .details:empty{ display:none; }

  .actions{
    display:grid;
    gap: var(--ctl-space-2-5);
    margin-top: var(--ctl-space-1);
  }
  .actions ::slotted(*){ width: 100%; }

  .footer{
    padding: var(--ctl-space-3) var(--ctl-space-4) var(--ctl-space-4);
    border-top: 1px solid var(--border);
    color: var(--muted);
    font-size: var(--ctl-font-size-sm);
    line-height: 1.35;
  }
  .footer:empty{ display:none; }

  .builtActions{
    display:grid;
    gap: var(--ctl-space-2-5);
  }

  .loading{
    display:none;
    padding: var(--ctl-space-4);
    gap: var(--ctl-space-2-5);
  }
  :host([loading]) .loading{ display:grid; }
  :host([loading]) .card{ display:none; }

  :host([disabled]){ opacity:.7; pointer-events:none; }

  /* status colors */
  :host([status="success"]) .icon{
    background: color-mix(in srgb, var(--ctl-color-success) 14%, var(--surface));
    border-color: color-mix(in srgb, var(--ctl-color-success) 24%, var(--border));
  }
  :host([status="pending"]) .icon{
    background: color-mix(in srgb, var(--ctl-color-warning) 14%, var(--surface));
    border-color: color-mix(in srgb, var(--ctl-color-warning) 24%, var(--border));
  }
  :host([status="error"]) .icon{
    background: color-mix(in srgb, var(--ctl-color-danger) 14%, var(--surface));
    border-color: color-mix(in srgb, var(--ctl-color-danger) 24%, var(--border));
  }

  ::slotted(template){ display:none; }
</style>

<div class="loading" part="loading">
  <ctl-skeleton variant="rect" height="18px" width="55%"></ctl-skeleton>
  <ctl-skeleton variant="rect" height="12px" width="86%"></ctl-skeleton>
  <ctl-skeleton variant="rect" height="12px" width="72%"></ctl-skeleton>
  <ctl-skeleton variant="rect" height="84px"></ctl-skeleton>
  <ctl-skeleton variant="rect" height="40px"></ctl-skeleton>
</div>

<div class="card" part="card">
  <div class="body" part="body">
    <div class="top" part="top">
      <div class="icon" part="icon" aria-hidden="true">
        <slot name="media" id="mediaSlot"></slot>
        <span id="defaultIcon"></span>
      </div>

      <div class="copy" part="copy">
        <h2 class="title" part="title" id="title"></h2>
        <p class="msg" part="message" id="message"></p>

        <div class="ref" part="reference" id="refRow" hidden>
          <span>Confirmation</span>
          <span class="code" id="refCode"></span>
          <ctl-button id="copyBtn" variant="secondary" size="sm">Copy</ctl-button>
        </div>
      </div>
    </div>

    <ctl-divider></ctl-divider>

    <div class="details" part="details">
      <slot name="details"></slot>
    </div>

    <div class="actions" part="actions">
      <slot name="actions" id="actionsSlot"></slot>

      <div class="builtActions" id="builtActions">
        <ctl-button id="primaryBtn"></ctl-button>
        <ctl-button id="secondaryBtn" variant="secondary" hidden></ctl-button>
      </div>
    </div>
  </div>

  <div class="footer" part="footer"><slot name="footer"></slot></div>
</div>
`;

function clampStr(s){ return String(s ?? "").trim(); }

function iconSvg(status) {
  if (status === "pending") {
    return `
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 8v5l3 2" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" stroke="currentColor" stroke-width="2.2"/>
      </svg>
    `;
  }
  if (status === "error") {
    return `
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 9v4" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
        <path d="M12 17h.01" stroke="currentColor" stroke-width="3.2" stroke-linecap="round"/>
        <path d="M10.3 4.3h3.4l7.2 12.5a2 2 0 0 1-1.7 3H4.8a2 2 0 0 1-1.7-3l7.2-12.5a2 2 0 0 1 1.8-1Z"
              stroke="currentColor" stroke-width="2.2" stroke-linejoin="round"/>
      </svg>
    `;
  }
  // success
  return `
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `;
}

export class CtlConfirmation extends HTMLElement {
  static get observedAttributes() {
    return [
      "status",
      "title",
      "message",
      "reference",
      "primary-label",
      "secondary-label",
      "loading",
      "disabled"
    ];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));

    this._title = this.shadowRoot.getElementById("title");
    this._message = this.shadowRoot.getElementById("message");
    this._refRow = this.shadowRoot.getElementById("refRow");
    this._refCode = this.shadowRoot.getElementById("refCode");
    this._copyBtn = this.shadowRoot.getElementById("copyBtn");

    this._mediaSlot = this.shadowRoot.getElementById("mediaSlot");
    this._defaultIcon = this.shadowRoot.getElementById("defaultIcon");

    this._actionsSlot = this.shadowRoot.getElementById("actionsSlot");
    this._builtActions = this.shadowRoot.getElementById("builtActions");
    this._primaryBtn = this.shadowRoot.getElementById("primaryBtn");
    this._secondaryBtn = this.shadowRoot.getElementById("secondaryBtn");

    this._onSlotChange = this._onSlotChange.bind(this);
    this._onPrimary = this._onPrimary.bind(this);
    this._onSecondary = this._onSecondary.bind(this);
    this._onCopy = this._onCopy.bind(this);
    
    // Store timer references for cleanup
    this._copyResetTimer = null;
  }

  connectedCallback() {
    if (!this.hasAttribute("status")) this.setAttribute("status", "success");

    // sensible defaults
    if (!this.hasAttribute("title")) this.title = this._defaultTitle(this.status);
    if (!this.hasAttribute("message")) this.message = this._defaultMessage(this.status);
    if (!this.hasAttribute("primary-label")) this.primaryLabel = this._defaultPrimary(this.status);
    if (!this.hasAttribute("secondary-label") && (this.status === "success" || this.status === "error")) {
      this.secondaryLabel = this.status === "success" ? "View details" : "Try again";
    }

    this._actionsSlot.addEventListener("slotchange", this._onSlotChange);
    this._primaryBtn.addEventListener("click", this._onPrimary);
    this._secondaryBtn.addEventListener("click", this._onSecondary);
    this._copyBtn.addEventListener("click", this._onCopy);

    this._sync();
    this._syncActionsVisibility();
  }

  disconnectedCallback() {
    this._actionsSlot.removeEventListener("slotchange", this._onSlotChange);
    this._primaryBtn.removeEventListener("click", this._onPrimary);
    this._secondaryBtn.removeEventListener("click", this._onSecondary);
    this._copyBtn.removeEventListener("click", this._onCopy);
    
    // Clear any pending copy reset timer
    if (this._copyResetTimer) {
      clearTimeout(this._copyResetTimer);
      this._copyResetTimer = null;
    }
  }

  attributeChangedCallback() {
    this._sync();
    this._syncActionsVisibility();
  }

  // attrs
  get status(){ return this.getAttribute("status") || "success"; }
  set status(v){
    const next = (v === "pending" || v === "error") ? v : "success";
    this.setAttribute("status", next);
  }

  get title(){ return this.getAttribute("title") || ""; }
  set title(v){ this.setAttribute("title", String(v ?? "")); }

  get message(){ return this.getAttribute("message") || ""; }
  set message(v){ this.setAttribute("message", String(v ?? "")); }

  get reference(){ return this.getAttribute("reference") || ""; }
  set reference(v){ v ? this.setAttribute("reference", String(v)) : this.removeAttribute("reference"); }

  get primaryLabel(){ return this.getAttribute("primary-label") || ""; }
  set primaryLabel(v){ this.setAttribute("primary-label", String(v ?? "")); }

  get secondaryLabel(){ return this.getAttribute("secondary-label") || ""; }
  set secondaryLabel(v){ v ? this.setAttribute("secondary-label", String(v)) : this.removeAttribute("secondary-label"); }

  get loading(){ return this.hasAttribute("loading"); }
  set loading(v){ v ? this.setAttribute("loading","") : this.removeAttribute("loading"); }

  get disabled(){ return this.hasAttribute("disabled"); }
  set disabled(v){ v ? this.setAttribute("disabled","") : this.removeAttribute("disabled"); }

  // internal
  _sync() {
    const st = this.status;

    // default icon only if no media slotted
    this._defaultIcon.innerHTML = iconSvg(st);

    const t = clampStr(this.title) || this._defaultTitle(st);
    const m = clampStr(this.message) || this._defaultMessage(st);

    this._title.textContent = t;
    this._message.textContent = m;

    const ref = clampStr(this.reference);
    this._refRow.hidden = !ref;
    this._refCode.textContent = ref;

    const p = clampStr(this.primaryLabel) || this._defaultPrimary(st);
    this._primaryBtn.textContent = p;

    const s = clampStr(this.secondaryLabel);
    this._secondaryBtn.hidden = !s;
    this._secondaryBtn.textContent = s;

    const dis = this.disabled || this.loading;
    this._primaryBtn.toggleAttribute("disabled", dis);
    this._secondaryBtn.toggleAttribute("disabled", dis);
    this._copyBtn.toggleAttribute("disabled", dis);
  }

  _syncActionsVisibility() {
    const assigned = this._actionsSlot.assignedNodes({ flatten: true }) || [];
    const hasCustom = assigned.some((n) => n.nodeType === Node.ELEMENT_NODE || (n.nodeType === Node.TEXT_NODE && String(n.textContent || "").trim()));
    this._builtActions.style.display = hasCustom ? "none" : "";
  }

  _onSlotChange() {
    // If media slot has content, hide default icon.
    const hasMedia = (this._mediaSlot.assignedElements({ flatten: true }) || []).length > 0;
    this._defaultIcon.style.display = hasMedia ? "none" : "";
    this._syncActionsVisibility();
  }

  _onPrimary(e) {
    e.preventDefault();
    if (this.disabled || this.loading) return;
    this.dispatchEvent(new CustomEvent("ctl-confirm", {
      bubbles: true,
      composed: true,
      detail: { status: this.status, reason: this.status }
    }));
  }

  _onSecondary(e) {
    e.preventDefault();
    if (this.disabled || this.loading) return;
    this.dispatchEvent(new CustomEvent("ctl-cancel", {
      bubbles: true,
      composed: true,
      detail: { status: this.status, reason: this.status }
    }));
  }

  async _onCopy(e) {
    e.preventDefault();
    const ref = clampStr(this.reference);
    if (!ref) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(ref);
      } else {
        // fallback
        const ta = document.createElement("textarea");
        ta.value = ref;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        ta.remove();
      }

      this._copyBtn.textContent = "Copied";
      // Clear any existing timer before setting a new one
      if (this._copyResetTimer) clearTimeout(this._copyResetTimer);
      this._copyResetTimer = setTimeout(() => {
        this._copyBtn.textContent = "Copy";
      }, 1000);
    } catch {
      // silent; app can listen to ctl-confirm/ctl-cancel events for feedback if desired
      this._copyBtn.textContent = "Copy failed";
      // Clear any existing timer before setting a new one
      if (this._copyResetTimer) clearTimeout(this._copyResetTimer);
      this._copyResetTimer = setTimeout(() => {
        this._copyBtn.textContent = "Copy";
      }, 1200);
    }
  }

  _defaultTitle(st) {
    if (st === "pending") return "Payment pending";
    if (st === "error") return "Payment failed";
    return "Reservation confirmed";
  }

  _defaultMessage(st) {
    if (st === "pending") return "We’re confirming your payment. This can take a moment. Please don’t close this page.";
    if (st === "error") return "Your payment didn’t go through. You can try again or use a different method.";
    return "You’re all set. Your unit is reserved and we’ve sent a confirmation email.";
  }

  _defaultPrimary(st) {
    if (st === "pending") return "Refresh status";
    if (st === "error") return "Retry payment";
    return "View units";
  }
}

customElements.define("ctl-confirmation", CtlConfirmation);
